import { useEffect, useState } from "react";
import { NavLink, useParams, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";
import { useFeatures } from "@/lib/useFeatures";
import {
  LayoutDashboard, ShoppingBag, CalendarClock, Grid3X3, UtensilsCrossed,
  Phone, BarChart3, Settings as SettingsIcon, LogOut, ShieldCheck, MoreHorizontal,
  MonitorSmartphone, ChefHat, Banknote, Globe, ChevronDown,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent, SheetTrigger, SheetHeader, SheetTitle } from "@/components/ui/sheet";

const NAV = [
  { to: "dashboard", label: "Dashboard", icon: LayoutDashboard },
  { to: "pos", label: "Take Order", icon: MonitorSmartphone, feature: "POS" },
  { to: "kitchen", label: "Kitchen", icon: ChefHat, feature: "KDS" },
  { to: "orders", label: "Orders", icon: ShoppingBag },
  { to: "bookings", label: "Bookings", icon: CalendarClock, feature: "BOOKINGS" },
  { to: "tables", label: "Tables", icon: Grid3X3, feature: "TABLES" },
  { to: "cash", label: "Cash", icon: Banknote, feature: "CASH_DRAWER" },
  { to: "menu", label: "Menu", icon: UtensilsCrossed },
  { to: "calls", label: "Calls", icon: Phone, feature: "AI_PHONE" },
  { to: "website", label: "Website", icon: Globe, feature: "WEBSITE" },
  { to: "usage", label: "Billing", icon: BarChart3 },
  { to: "settings", label: "Settings", icon: SettingsIcon },
];

const MOBILE_PRIMARY = ["dashboard", "orders", "pos"];

function NavItem({ rid, n, onNav }) {
  const Icon = n.icon;
  return (
    <NavLink
      to={`/r/${rid}/${n.to}`}
      onClick={onNav}
      data-testid={`nav-${n.to}`}
      className={({ isActive }) =>
        `flex flex-col items-center justify-center gap-1 rounded-xl px-3 py-2 min-w-[64px] shrink-0 transition-colors ${
          isActive ? "bg-primary/10 text-primary font-semibold" : "text-foreground/60 hover:text-foreground hover:bg-secondary"
        }`
      }
    >
      <Icon className="h-[20px] w-[20px]" />
      <span className="text-[11px] leading-none">{n.label}</span>
    </NavLink>
  );
}

export default function Layout({ children }) {
  const { rid } = useParams();
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [restaurant, setRestaurant] = useState(null);
  const [moreOpen, setMoreOpen] = useState(false);
  const features = useFeatures(rid);

  useEffect(() => {
    if (rid) api.get(`/restaurants/${rid}`).then((r) => setRestaurant(r.data)).catch(() => {});
  }, [rid]);

  const nav = NAV.filter((n) => !n.feature || features === null || features[n.feature]);
  const initials = (user?.name || "U").split(" ").map((w) => w[0]).slice(0, 2).join("").toUpperCase();
  const doLogout = () => { logout(); navigate("/login"); };

  return (
    <div className="min-h-screen flex flex-col bg-background">
      {/* Top bar */}
      <header className="sticky top-0 z-30 flex items-center justify-between border-b border-border bg-card/90 backdrop-blur px-4 sm:px-6 h-16">
        <div className="flex items-center gap-3 min-w-0">
          <img src="/dineflo-logo.png" alt="DineFlo" className="h-9 w-9 rounded-lg object-contain bg-black ring-1 ring-border shrink-0" />
          <span className="font-display font-extrabold text-lg tracking-tight hidden sm:block">DineFlo</span>
          <div className="hidden md:flex items-center gap-1.5 pl-3 ml-1 border-l border-border text-sm text-muted-foreground min-w-0">
            <span className="truncate font-medium text-foreground" data-testid="active-restaurant-name">{restaurant?.name || "…"}</span>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <div className="text-right hidden sm:block leading-tight">
            <p className="text-sm font-medium truncate max-w-[160px]">{user?.name}</p>
            <p className="text-[11px] text-muted-foreground">{(user?.role || "").replace(/_/g, " ").toLowerCase()}</p>
          </div>
          <div className="h-9 w-9 rounded-full bg-primary/10 text-primary font-semibold flex items-center justify-center text-sm shrink-0" data-testid="user-avatar">{initials}</div>
          <Button variant="ghost" size="icon" className="h-9 w-9" data-testid="logout-button" onClick={doLogout}><LogOut className="h-4 w-4" /></Button>
        </div>
      </header>

      <main className="flex-1 p-5 sm:p-8 pb-28 max-w-[1400px] w-full mx-auto fade-up">{children}</main>

      {/* Desktop / tablet bottom icon nav */}
      <nav className="hidden md:flex fixed bottom-0 inset-x-0 z-40 justify-center px-4 pb-3 pointer-events-none">
        <div className="pointer-events-auto flex items-center gap-1 rounded-2xl border border-border bg-card/95 backdrop-blur shadow-lg px-2 py-2 overflow-x-auto no-scrollbar max-w-full">
          {nav.map((n) => <NavItem key={n.to} rid={rid} n={n} />)}
          {user?.role === "PLATFORM_ADMIN" && (
            <NavLink to="/admin" data-testid="nav-admin-console"
              className="flex flex-col items-center justify-center gap-1 rounded-xl px-3 py-2 min-w-[64px] shrink-0 text-foreground/60 hover:text-foreground hover:bg-secondary">
              <ShieldCheck className="h-[20px] w-[20px]" /><span className="text-[11px] leading-none">Admin</span>
            </NavLink>
          )}
        </div>
      </nav>

      {/* Mobile bottom tab bar */}
      <nav className="md:hidden fixed bottom-0 inset-x-0 z-40 grid grid-cols-4 border-t border-border bg-card">
        {MOBILE_PRIMARY.map((to) => {
          const n = NAV.find((x) => x.to === to);
          const Icon = n.icon;
          return (
            <NavLink key={to} to={`/r/${rid}/${to}`} data-testid={`mobile-nav-${to}`}
              className={({ isActive }) => `flex flex-col items-center justify-center gap-1 py-2.5 ${isActive ? "text-primary" : "text-foreground/60"}`}>
              <Icon className="h-5 w-5" />
              <span className="text-[10px]">{to === "dashboard" ? "Home" : to === "pos" ? "POS" : n.label}</span>
            </NavLink>
          );
        })}
        <Sheet open={moreOpen} onOpenChange={setMoreOpen}>
          <SheetTrigger asChild>
            <button data-testid="mobile-menu-button" className="flex flex-col items-center justify-center gap-1 py-2.5 text-foreground/60">
              <MoreHorizontal className="h-5 w-5" /><span className="text-[10px]">More</span>
            </button>
          </SheetTrigger>
          <SheetContent side="bottom" className="rounded-t-2xl max-h-[80vh] overflow-y-auto">
            <SheetHeader><SheetTitle className="font-display">{restaurant?.name || "DineFlo"}</SheetTitle></SheetHeader>
            <div className="grid grid-cols-3 gap-2 mt-4">
              {nav.map((n) => {
                const Icon = n.icon;
                return (
                  <NavLink key={n.to} to={`/r/${rid}/${n.to}`} onClick={() => setMoreOpen(false)} data-testid={`nav-${n.to}`}
                    className={({ isActive }) => `flex flex-col items-center gap-1.5 rounded-xl border p-3 text-xs ${isActive ? "border-primary/40 bg-primary/10 text-primary" : "border-border"}`}>
                    <Icon className="h-5 w-5" /> {n.label}
                  </NavLink>
                );
              })}
              {user?.role === "PLATFORM_ADMIN" && (
                <NavLink to="/admin" onClick={() => setMoreOpen(false)} data-testid="nav-admin-console"
                  className="flex flex-col items-center gap-1.5 rounded-xl border border-border p-3 text-xs">
                  <ShieldCheck className="h-5 w-5" /> Admin
                </NavLink>
              )}
            </div>
            <Button variant="outline" className="w-full mt-4 gap-2 h-11" onClick={doLogout} data-testid="logout-button-mobile"><LogOut className="h-4 w-4" /> Log out</Button>
          </SheetContent>
        </Sheet>
      </nav>
    </div>
  );
}
